import { useEffect } from 'react';

interface UseKeyboardNavigationProps {
  onPrevious: () => void;
  onNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
  enabled?: boolean;
}

/**
 * Custom hook for handling keyboard navigation (arrow keys)
 * Listens for left/right arrow key presses and triggers navigation
 */
export function useKeyboardNavigation({
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
  enabled = true
}: UseKeyboardNavigationProps) {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle arrow keys when not in an input field
      const target = event.target as HTMLElement;
      const isInputField = target.tagName === 'INPUT' || 
                          target.tagName === 'TEXTAREA' || 
                          target.contentEditable === 'true';
      
      if (isInputField) return;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          if (hasPrevious) {
            onPrevious();
          }
          break;
        case 'ArrowRight':
          event.preventDefault();
          if (hasNext) {
            onNext();
          }
          break;
        default:
          break;
      }
    };

    // Add event listener to document
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup function
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onPrevious, onNext, hasPrevious, hasNext, enabled]);
}
